import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useProject } from "@/lib/ddb-hooks";
import { useNesting } from "@/lib/cabinets/useNesting";
import type { NestBin, NestPlacement, NestSheet } from "@/lib/cabinets/nest";
import type { Id } from "@/lib/types";

const NUM2 = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });
const PCT  = new Intl.NumberFormat("en-US", { style: "percent", maximumFractionDigits: 1 });

export function CutListPanel() {
  const { id } = useParams();
  const nav = useNavigate();
  const numId = id ? Number(id) : null;
  const { data: project } = useProject(numId);
  const { nesting, isLoading, error } = useNesting(numId);
  const [zoomed, setZoomed] = useState<{ bin: NestBin; sheet: NestSheet } | null>(null);

  if (!id || Number.isNaN(Number(id))) return <div className="empty-state">Invalid project id.</div>;
  if (isLoading) return <div className="empty-state">Computing cut list…</div>;
  if (error) return <div className="empty-state">Couldn't compute nesting: {error.message}</div>;
  if (!nesting) return <div className="empty-state">No data.</div>;

  const totalSheets = nesting.bins.reduce((a, b) => a + b.totalSheets, 0);

  return (
    <div style={{ padding: "16px 24px", display: "grid", gridTemplateRows: "auto 1fr", height: "100%", gap: 16 }}>
      <header style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Cut List</h2>
        <span className="muted">·</span>
        <span className="muted">{project?.name ?? "—"}</span>
        <span style={{ flex: 1 }} />
        <Stat label="Sheets"      value={String(totalSheets)} />
        <Stat label="Materials"   value={String(nesting.bins.length)} />
        <Stat label="Overall waste" value={PCT.format(nesting.overallWastePercent)} highlight={nesting.overallWastePercent > 0.25} />
        <button className="ghost" onClick={() => nav(`/projects/${id}/design`)}>← Back to design</button>
      </header>

      <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 24 }}>
        {nesting.bins.length === 0 ? (
          <div className="muted">No materials nested. Add cabinets in the design view first.</div>
        ) : nesting.bins.map((bin) => (
          <BinSection key={bin.materialId} bin={bin} onZoom={(sheet) => setZoomed({ bin, sheet })} />
        ))}

        {nesting.unplaceable.length > 0 && (
          <section className="card" style={{ borderLeft: "3px solid var(--danger)" }}>
            <h3 style={{ marginTop: 0, color: "var(--danger)" }}>Unplaceable parts</h3>
            <ul className="muted" style={{ fontSize: 13, marginLeft: 0, paddingLeft: 20 }}>
              {nesting.unplaceable.map((u, i) => (
                <li key={i}><b>{u.partName}</b> — {u.reason}</li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {zoomed && <ZoomedSheet bin={zoomed.bin} sheet={zoomed.sheet} onClose={() => setZoomed(null)} />}
    </div>
  );
}

// ── Per-material section ─────────────────────────────────────────

interface BinSectionProps {
  bin: NestBin;
  onZoom: (sheet: NestSheet) => void;
}

function BinSection({ bin, onZoom }: BinSectionProps) {
  return (
    <section className="card" style={{ padding: 16 }}>
      <header style={{ display: "flex", alignItems: "baseline", gap: 16, marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>{bin.materialName}</h3>
        <span className="muted" style={{ fontSize: 12 }}>
          {bin.totalSheets} sheets ({bin.sheetW}″ × {bin.sheetH}″) · waste {PCT.format(bin.wastePercent)}
        </span>
        {bin.grainDir !== "none" && (
          <span className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
            grain: {bin.grainDir}
          </span>
        )}
      </header>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
        {bin.sheets.map((s) => (
          <SheetThumbnail
            key={s.sheetIndex}
            bin={bin}
            sheet={s}
            onClick={() => onZoom(s)}
          />
        ))}
      </div>
    </section>
  );
}

// ── Sheet rendering ──────────────────────────────────────────────

const VIEW_PAD = 6;

interface SheetThumbnailProps {
  bin: NestBin;
  sheet: NestSheet;
  onClick: () => void;
}

function SheetThumbnail({ bin, sheet, onClick }: SheetThumbnailProps) {
  const usedPct = sheet.usedArea / (bin.sheetW * bin.sheetH);
  return (
    <button
      onClick={onClick}
      className="ghost"
      style={{
        padding: 8,
        display: "flex",
        flexDirection: "column",
        gap: 6,
        textAlign: "left",
        background: "var(--bg-elev)",
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600 }}>
        Sheet {sheet.sheetIndex + 1}
      </div>
      <SheetSvg bin={bin} sheet={sheet} maxPx={160} interactive={false} />
      <div className="muted" style={{ fontSize: 11 }}>
        {sheet.placements.length} parts · {PCT.format(usedPct)} used
      </div>
    </button>
  );
}

interface SheetSvgProps {
  bin: NestBin;
  sheet: NestSheet;
  maxPx: number;
  interactive: boolean;
  onHover?: (p: NestPlacement | null) => void;
}

function SheetSvg({ bin, sheet, maxPx, interactive, onHover }: SheetSvgProps) {
  const aspect = bin.sheetW / bin.sheetH;
  const width  = aspect > 1 ? maxPx : maxPx * aspect;
  const height = aspect > 1 ? maxPx / aspect : maxPx;
  const viewW = bin.sheetW + VIEW_PAD * 2;
  const viewH = bin.sheetH + VIEW_PAD * 2;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${viewW} ${viewH}`}
      style={{ background: "#1a232c", border: "1px solid var(--line)", borderRadius: 3 }}
    >
      {/* sheet outline */}
      <rect
        x={VIEW_PAD}
        y={VIEW_PAD}
        width={bin.sheetW}
        height={bin.sheetH}
        fill="#2a3340"
        stroke="#5b6b80"
        strokeWidth={0.6}
      />
      {/* placements */}
      {sheet.placements.map((p, i) => (
        <PlacementRect
          key={i}
          placement={p}
          interactive={interactive}
          onHover={onHover}
        />
      ))}
    </svg>
  );
}

function PlacementRect({
  placement, interactive, onHover,
}: { placement: NestPlacement; interactive: boolean; onHover?: (p: NestPlacement | null) => void }) {
  // y-axis flip: SVG y grows downward, but we want sheet (0,0) at bottom-left
  // for a "looking-down-at-the-sheet" convention.
  const y = VIEW_PAD; // we'll layout via transform
  const color = cabinetColor(placement.cabinetId);
  return (
    <g
      transform={`translate(${VIEW_PAD + placement.x}, ${y + (placement.y)})`}
      onMouseEnter={interactive ? () => onHover?.(placement) : undefined}
      onMouseLeave={interactive ? () => onHover?.(null) : undefined}
      style={{ cursor: interactive ? "crosshair" : "default" }}
    >
      <rect
        width={placement.w}
        height={placement.h}
        fill={color}
        opacity={0.78}
        stroke="#0b1219"
        strokeWidth={0.4}
      />
      {placement.w > 6 && placement.h > 6 && (
        <text
          x={placement.w / 2}
          y={placement.h / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={Math.min(placement.w / 6, placement.h / 4, 4)}
          fill="#0b1219"
          style={{ pointerEvents: "none", userSelect: "none", fontWeight: 600 }}
        >
          #{placement.cabinetId}
        </text>
      )}
    </g>
  );
}

// ── Zoomed-sheet modal ───────────────────────────────────────────

function ZoomedSheet({
  bin, sheet, onClose,
}: { bin: NestBin; sheet: NestSheet; onClose: () => void }) {
  const [hover, setHover] = useState<NestPlacement | null>(null);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.7)",
        display: "grid", placeItems: "center",
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ maxWidth: "85vw", maxHeight: "90vh", padding: 16, overflow: "auto", display: "grid", gap: 12 }}
      >
        <header style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
          <h3 style={{ margin: 0 }}>{bin.materialName} — Sheet {sheet.sheetIndex + 1}</h3>
          <span className="muted">{bin.sheetW}″ × {bin.sheetH}″ · {sheet.placements.length} parts</span>
          <span style={{ flex: 1 }} />
          <button className="ghost" onClick={onClose}>Close</button>
        </header>
        <SheetSvg bin={bin} sheet={sheet} maxPx={720} interactive onHover={setHover} />
        <div style={{ minHeight: 32, fontSize: 13 }}>
          {hover ? (
            <span>
              <b>{hover.partName}</b> — cabinet #{hover.cabinetId} ({hover.cabinetClassId}) ·
              {" "}{NUM2.format(hover.w)}″ × {NUM2.format(hover.h)}″
              {hover.rotated ? " (rotated)" : ""}
            </span>
          ) : (
            <span className="muted">Hover a part for details.</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Cabinet → colour mapping ─────────────────────────────────────

const CABINET_PALETTE = [
  "#d18b53", "#6b9bd2", "#6bbe88", "#c97070",
  "#a47148", "#9b7fd2", "#d2a86b", "#4fb39e",
  "#d96b9b", "#7fb96b", "#b96b6b", "#6b8db9",
];

function cabinetColor(id: Id): string {
  const i = Math.abs(id) % CABINET_PALETTE.length;
  return CABINET_PALETTE[i] ?? "#888";
}

// ── Tiny stat widget ─────────────────────────────────────────────

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
      <span className="label" style={{ fontSize: 10 }}>{label}</span>
      <span style={{ fontWeight: 700, color: highlight ? "var(--danger)" : "var(--accent)" }}>{value}</span>
    </div>
  );
}
